// import { vansiteGetGeolocationJson } from '../util/api';

import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';

import {
  denormalisedEntities,
  updatedEntities,
  updateStoryblokEntities,
  updateTranslationEntities,
} from '../util/data';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const ADD_MARKETPLACE_ENTITIES = 'app/marketplaceData/ADD_MARKETPLACE_ENTITIES';
// export const GEO_LOCATION_JSON = 'app/marketplaceData/GEO_LOCATION_JSON';
export const ADD_STORYBLOK_ENTITIES = 'app/marketplaceData/ADD_STORYBLOK_ENTITIES';
export const ADD_TRANSLATION_ENTITIES = 'app/marketplaceData/ADD_TRANSLATION_ENTITIES';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.MarketplaceData} */
const initialState = {
  // Database of all the fetched entities.
  entities: {
    entitiesUpdateId: 0,
  },
  storyblokEntities: {
    entitiesUpdateId: 0,
  },
  translationEntities: {
    entitiesUpdateId: 0,
  },
  geolocationJson: null,
};

const merge = (state, sdkResponse, { sparse = false } = {}) => {
  let apiResponse = sdkResponse.data;
  return {
    ...state,
    entities: {
      ...updatedEntities({ ...state.entities }, apiResponse, { sparse }),
      entitiesUpdateId: state.entities.entitiesUpdateId + 1,
    },
  };
};

const mergeStoryblok = (state, storyblokResponse) => {
  return {
    ...state,
    storyblokEntities: {
      ...updateStoryblokEntities({ ...state.storyblokEntities }, storyblokResponse),
      entitiesUpdateId: state.storyblokEntities.entitiesUpdateId + 1,
    },
  };
};

const mergeTranslation = (state, translationResponse) => {
  return {
    ...state,
    translationEntities: {
      ...updateTranslationEntities({ ...state.translationEntities }, translationResponse),
      entitiesUpdateId: state.translationEntities.entitiesUpdateId + 1,
    },
  };
};

export default function marketplaceDataReducer(state = initialState, action = {}) {
  const { type, payload, meta } = action;
  switch (type) {
    case ADD_MARKETPLACE_ENTITIES:
      return merge(state, payload, meta);
    case ADD_STORYBLOK_ENTITIES:
      return mergeStoryblok(state, payload);
    case ADD_TRANSLATION_ENTITIES:
      return mergeTranslation(state, payload);
    // case GEO_LOCATION_JSON:
    //   return { ...state, geolocationJson: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

/**
 * Get the denormalised listing entities with the given IDs
 *
 * @param {object} entities marketplace entries
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getListingsById = (entities, listingIds) => {
  const resources = listingIds.map((id) => ({
    id,
    type: 'listing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(entities, resources, throwIfNotFound);
};

/**
 * Get the denormalised entities from the given entity references.
 *
 * @param {object} entities the full Redux store
 *
 * @param {Array<{ id, type }} entityRefs References to entities that
 * we want to query from the data. Currently we expect that all the
 * entities have the same type.
 *
 * @returns {Array<Object>} denormalised entities
 */
export const getMarketplaceEntities = (entities, entityRefs) => {
  const throwIfNotFound = false;
  return denormalisedEntities(entities, entityRefs, throwIfNotFound);
};

export const getStoryblokEntities = (storyblokEntities, ref) => {
  return (
    (storyblokEntities[ref.type] &&
      storyblokEntities[ref.type][ref.slug] &&
      storyblokEntities[ref.type][ref.slug]) ||
    null
  );
};

export const getTranslationEntities = (translationEntities, ref) => {
  const defaultLocale =
    translationEntities[ref.type] &&
    translationEntities[ref.type][ref.id] &&
    translationEntities[ref.type][ref.id].defaultLocale;
  const translations =
    translationEntities[ref.type] &&
    translationEntities[ref.type][ref.id] &&
    translationEntities[ref.type][ref.id][ref.lang];
  return { defaultLocale, translations: translations || {} };
};

// ================ Action creators ================ //

/**
 * Normalise an SDK response into marketplaceData.entities.
 *
 * Pass `sparse: true` for queries with a restricted fieldset (listing cards:
 * `fields.listing`, `limit.images`, few image variants). Existing entities are
 * then merged instead of replaced, so a late card response cannot wipe the
 * publicData, images or variants a full fetch (showListing) already stored.
 * Full fetches keep the default replace semantics, so keys removed on edit
 * disappear.
 *
 * @param {object} sdkResponse SDK response (`{ data: { data, included } }`)
 * @param {{ sparse?: boolean }} [options]
 */
export const addMarketplaceEntities = (sdkResponse, { sparse = false } = {}) => ({
  type: ADD_MARKETPLACE_ENTITIES,
  payload: sdkResponse,
  ...(sparse ? { meta: { sparse: true } } : {}),
});

export const addStoryblokEntities = (storyblokResponse) => ({
  type: ADD_STORYBLOK_ENTITIES,
  payload: storyblokResponse,
});

export const addTranslationEntities = (translationResponse) => ({
  type: ADD_TRANSLATION_ENTITIES,
  payload: translationResponse,
});

// export const generateGeoLocationJsonSuccess = payload => ({
//   type: GEO_LOCATION_JSON,
//   payload: payload,
// });

// export const generateGeoLocationJson = () => () => {
//   vansiteGetGeolocationJson()
//     .then(res => {
//     dispatch(generateGeoLocationJsonSuccess(res.data));
//   });
// };
